import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import {
  FileText,
  FileSignature,
  Plus,
  Lock,
  Unlock,
  Download,
  History,
  Upload,
  Trash2,
  ShieldAlert,
  RotateCcw,
} from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { UniversalCombobox } from '@/components/common/UniversalCombobox';
import { useAuth } from '@/features/auth';
import {
  useMatterDocuments,
  useMatterDocument,
  useDocumentMutations,
} from '../hooks';
import type { Matter, MatterDocument } from '../types';

interface DocumentsCardProps {
  matter: Matter;
}

/**
 * Case 360° (Lot 2) Stage 10 — the matter's documents. Versioned metadata over
 * the file store: upload creates version 1, every re-upload appends an
 * immutable version, rollback re-points the current pointer. SECRET documents
 * are visible but locked for non-Lead/Partner/MD users, who can break-glass
 * request access; an MD / matter partner grants 4h via the same card.
 */
export function DocumentsCard({ matter }: DocumentsCardProps) {
  const { t } = useTranslation();
  const { hasAccess, user } = useAuth();
  const { data: documents, isLoading } = useMatterDocuments(matter?.id);
  const {
    createDocumentMutation,
    addVersionMutation,
    updateDocumentMutation,
    rollbackDocumentMutation,
    deleteDocumentMutation,
    downloadVersionMutation,
    requestBreakGlassMutation,
    grantBreakGlassMutation,
  } = useDocumentMutations();

  // Files are streamed from MinIO through the authorized API (no public URL).
  const downloadVersion = (
    documentId: number,
    versionId: number,
    fileName: string,
  ) => {
    downloadVersionMutation.mutate({ documentId, versionId, fileName });
  };

  // Optimistic client-side gates — the server re-checks everything.
  const globalRoleType = user?.role?.type;
  const isTeamPartner =
    matter?.partners?.some((p) => p?.id === user?.id) ?? false;
  const isTeamLead =
    matter?.responsibleAttorneys?.some((a) => a?.id === user?.id) ?? false;
  const isSecretController =
    globalRoleType === 'MD' ||
    globalRoleType === 'MANAGER' ||
    isTeamPartner ||
    isTeamLead;
  const canApproveBreakGlass =
    hasAccess('document', 'break-glass') &&
    (globalRoleType === 'MD' || globalRoleType === 'MANAGER' || isTeamPartner);
  const isClosed = matter?.phase === 'CLOSED';
  const canCreate = hasAccess('document', 'create') && !isClosed;
  const canUploadVersion = hasAccess('document', 'upload') && !isClosed;
  const canUpdate = hasAccess('document', 'update') && !isClosed;
  const canDelete =
    hasAccess('document', 'delete') && isSecretController && !isClosed;

  // --- add document dialog ---
  const [addOpen, setAddOpen] = React.useState(false);
  const [addFile, setAddFile] = React.useState<File | null>(null);
  const [addName, setAddName] = React.useState('');
  const [addSecret, setAddSecret] = React.useState(false);
  const [addEngagement, setAddEngagement] = React.useState(false);

  // At most one active engagement letter per matter (server-enforced).
  const hasEngagementLetter =
    documents?.some((d) => d?.isEngagementLetter) ?? false;

  const openAdd = (asEngagementLetter = false) => {
    setAddFile(null);
    setAddName('');
    setAddSecret(false);
    setAddEngagement(asEngagementLetter);
    setAddOpen(true);
  };

  const handleCreate = async () => {
    if (!addFile || !matter) return;
    await createDocumentMutation.mutateAsync({
      matterId: matter.id,
      file: addFile,
      ...(addName.trim() ? { name: addName.trim() } : {}),
      ...(addSecret ? { isSecret: true } : {}),
      ...(addEngagement ? { isEngagementLetter: true } : {}),
    });
    setAddOpen(false);
  };

  // --- versions / history dialog ---
  const [historyDoc, setHistoryDoc] = React.useState<MatterDocument | null>(null);
  const { data: historyDetail } = useMatterDocument(historyDoc?.id, !!historyDoc);

  // --- new version upload (hidden input per document) ---
  const versionInputRef = React.useRef<HTMLInputElement | null>(null);
  const versionDocRef = React.useRef<number | null>(null);

  const pickVersionFile = (documentId: number) => {
    versionDocRef.current = documentId;
    versionInputRef.current?.click();
  };

  const handleVersionFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !versionDocRef.current || !matter) return;
    await addVersionMutation.mutateAsync({
      documentId: versionDocRef.current,
      matterId: matter.id,
      file,
    });
  };

  // --- break-glass dialogs ---
  const [requestDoc, setRequestDoc] = React.useState<MatterDocument | null>(null);
  const [requestReason, setRequestReason] = React.useState('');
  const [grantDoc, setGrantDoc] = React.useState<MatterDocument | null>(null);
  const [grantAuditorId, setGrantAuditorId] = React.useState<number | undefined>();
  const [grantReason, setGrantReason] = React.useState('');

  const handleRequest = async () => {
    if (!requestDoc || !requestReason.trim() || !matter) return;
    await requestBreakGlassMutation.mutateAsync({
      documentId: requestDoc.id,
      matterId: matter.id,
      data: { reason: requestReason.trim() },
    });
    setRequestDoc(null);
    setRequestReason('');
  };

  const handleGrant = async () => {
    if (!grantDoc || !grantAuditorId || !grantReason.trim() || !matter) return;
    await grantBreakGlassMutation.mutateAsync({
      documentId: grantDoc.id,
      matterId: matter.id,
      data: { auditorId: grantAuditorId, reason: grantReason.trim() },
    });
    setGrantDoc(null);
    setGrantAuditorId(undefined);
    setGrantReason('');
  };

  // --- delete confirm ---
  const [deleteDoc, setDeleteDoc] = React.useState<MatterDocument | null>(null);

  const handleDelete = async () => {
    if (!deleteDoc || !matter) return;
    await deleteDocumentMutation.mutateAsync({
      documentId: deleteDoc.id,
      matterId: matter.id,
    });
    setDeleteDoc(null);
  };

  const toggleSecret = async (doc: MatterDocument) => {
    if (!matter) return;
    await updateDocumentMutation.mutateAsync({
      documentId: doc.id,
      matterId: matter.id,
      data: { isSecret: !doc?.isSecret },
    });
  };

  const handleRollback = async (versionId: number) => {
    if (!historyDoc || !matter) return;
    await rollbackDocumentMutation.mutateAsync({
      documentId: historyDoc.id,
      matterId: matter.id,
      versionId,
    });
  };

  return (
    <Card className="bg-white border-slate-200 shadow-sm hover:shadow-md transition-shadow">
      <CardHeader className="pb-3 border-b border-slate-100">
        <CardTitle className="text-lg font-semibold text-slate-900 flex items-center justify-between gap-2">
          <span className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-slate-400" />
            {t('document.cardTitle')}
          </span>
          {canCreate && (
            <span className="flex items-center gap-2">
              {!hasEngagementLetter && (
                <Button size="sm" variant="outline" onClick={() => openAdd(true)}>
                  <FileSignature className="mr-1 h-4 w-4 text-emerald-600" />
                  {t('document.uploadEngagement')}
                </Button>
              )}
              <Button size="sm" variant="outline" onClick={() => openAdd()}>
                <Plus className="mr-1 h-4 w-4" />
                {t('document.add')}
              </Button>
            </span>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-4">
        {isLoading ? (
          <p className="text-sm text-slate-500">{t('loading')}</p>
        ) : !documents?.length ? (
          <p className="text-sm text-slate-500">{t('document.empty')}</p>
        ) : (
          <ul className="space-y-3 max-h-72 overflow-y-auto pr-1">
            {documents.map((doc) => (
              <li
                key={doc.id}
                className={`rounded-md border px-3 py-2 text-sm ${
                  doc?.locked
                    ? 'border-red-200 bg-red-50'
                    : 'border-slate-200 bg-white'
                }`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="flex min-w-0 items-center gap-2 font-medium text-slate-800">
                    {doc?.isSecret ? (
                      <Lock className="h-4 w-4 shrink-0 text-red-500" />
                    ) : (
                      <FileText className="h-4 w-4 shrink-0 text-slate-400" />
                    )}
                    <span className="truncate">{doc?.name}</span>
                    {doc?.isEngagementLetter && (
                      <Badge className="border border-emerald-300 bg-emerald-50 text-emerald-700">
                        {t('document.engagementLetter')}
                      </Badge>
                    )}
                    {doc?.isSecret && (
                      <Badge className="border border-red-300 bg-red-50 text-red-700">
                        {t('document.secret')}
                      </Badge>
                    )}
                  </span>
                  {!doc?.locked && doc?.currentVersion && (
                    <span className="shrink-0 text-xs text-slate-500">
                      v{doc.currentVersion.version}
                    </span>
                  )}
                </div>

                {doc?.locked ? (
                  <div className="mt-1 flex items-center justify-between gap-2">
                    <span className="text-xs text-red-700">
                      {t('document.lockedHint')}
                    </span>
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-red-300 text-red-700 hover:bg-red-100"
                      onClick={() => {
                        setRequestReason('');
                        setRequestDoc(doc);
                      }}
                    >
                      <ShieldAlert className="mr-1 h-3.5 w-3.5" />
                      {t('document.requestAccess')}
                    </Button>
                  </div>
                ) : (
                  <div className="mt-1 flex flex-wrap items-center justify-between gap-2">
                    <span className="text-xs text-slate-400">
                      {doc?.createdAt
                        ? format(new Date(doc.createdAt), 'dd MMM yyyy')
                        : ''}
                      {doc?.breakGlassExpiresAt && (
                        <span className="ml-2 text-amber-600">
                          {t('document.accessUntil', {
                            time: format(new Date(doc.breakGlassExpiresAt), 'HH:mm'),
                          })}
                        </span>
                      )}
                    </span>
                    <span className="flex items-center gap-1">
                      {doc?.currentVersion?.id && (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-7 w-7 p-0"
                          title={t('downloadDocument')}
                          disabled={downloadVersionMutation.isPending}
                          onClick={() =>
                            downloadVersion(
                              doc.id,
                              doc.currentVersion!.id,
                              doc?.currentVersion?.file?.originalName ?? doc?.name,
                            )
                          }
                        >
                          <Download className="h-3.5 w-3.5" />
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 w-7 p-0"
                        title={t('document.history')}
                        onClick={() => setHistoryDoc(doc)}
                      >
                        <History className="h-3.5 w-3.5" />
                      </Button>
                      {canUploadVersion && (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-7 w-7 p-0"
                          title={t('document.newVersion')}
                          onClick={() => pickVersionFile(doc.id)}
                        >
                          <Upload className="h-3.5 w-3.5" />
                        </Button>
                      )}
                      {canUpdate && isSecretController && (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-7 w-7 p-0"
                          title={doc?.isSecret ? t('document.unsetSecret') : t('document.setSecret')}
                          onClick={() => void toggleSecret(doc)}
                        >
                          {doc?.isSecret ? (
                            <Unlock className="h-3.5 w-3.5 text-red-500" />
                          ) : (
                            <Lock className="h-3.5 w-3.5" />
                          )}
                        </Button>
                      )}
                      {doc?.isSecret && canApproveBreakGlass && (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-7 w-7 p-0"
                          title={t('document.grantAccess')}
                          onClick={() => {
                            setGrantAuditorId(undefined);
                            setGrantReason('');
                            setGrantDoc(doc);
                          }}
                        >
                          <ShieldAlert className="h-3.5 w-3.5 text-amber-600" />
                        </Button>
                      )}
                      {canDelete && (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-7 w-7 p-0 text-red-500 hover:text-red-700"
                          title={t('delete')}
                          onClick={() => setDeleteDoc(doc)}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      )}
                    </span>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
        <input
          ref={versionInputRef}
          type="file"
          className="hidden"
          onChange={(e) => void handleVersionFile(e)}
        />
      </CardContent>

      {/* Add document */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-4 w-4 text-slate-500" />
              {t('document.addDialogTitle')}
            </DialogTitle>
            <DialogDescription>{t('document.addDialogDescription')}</DialogDescription>
          </DialogHeader>
          <div className="my-2 space-y-4">
            <div>
              <label className="mb-2 block text-sm font-medium">
                {t('document.fileLabel')}
              </label>
              <Input
                type="file"
                onChange={(e) => setAddFile(e.target.files?.[0] ?? null)}
              />
            </div>
            <div>
              <label className="mb-2 block text-sm font-medium">
                {t('documentName')}
              </label>
              <Input
                value={addName}
                onChange={(e) => setAddName(e.target.value)}
                placeholder={addFile?.name ?? t('document.namePlaceholder')}
                maxLength={255}
              />
            </div>
            {!hasEngagementLetter && (
              <label className="flex items-center gap-2 text-sm">
                <Checkbox
                  checked={addEngagement}
                  onCheckedChange={(v) => setAddEngagement(v === true)}
                />
                <span className="flex items-center gap-1">
                  <FileSignature className="h-3.5 w-3.5 text-emerald-600" />
                  {t('document.markEngagement')}
                </span>
              </label>
            )}
            {isSecretController && (
              <label className="flex items-center gap-2 text-sm">
                <Checkbox
                  checked={addSecret}
                  onCheckedChange={(v) => setAddSecret(v === true)}
                />
                <span className="flex items-center gap-1">
                  <Lock className="h-3.5 w-3.5 text-red-500" />
                  {t('document.markSecret')}
                </span>
              </label>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setAddOpen(false)}
              disabled={createDocumentMutation.isPending}
            >
              {t('cancel')}
            </Button>
            <Button
              onClick={() => void handleCreate()}
              disabled={!addFile || createDocumentMutation.isPending}
            >
              {createDocumentMutation.isPending ? t('processing') : t('document.upload')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Version history + rollback */}
      <Dialog open={!!historyDoc} onOpenChange={(o) => !o && setHistoryDoc(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="h-4 w-4 text-slate-500" />
              {t('document.historyDialogTitle', { name: historyDoc?.name ?? '' })}
            </DialogTitle>
            <DialogDescription>{t('document.historyDialogDescription')}</DialogDescription>
          </DialogHeader>
          <ul className="my-2 max-h-80 space-y-2 overflow-y-auto pr-1">
            {(historyDetail?.versions ?? []).map((v) => {
              const isCurrent = historyDetail?.currentVersionId === v?.id;
              return (
                <li
                  key={v.id}
                  className={`flex items-center justify-between gap-2 rounded-md border px-3 py-2 text-sm ${
                    isCurrent ? 'border-green-300 bg-green-50' : 'border-slate-200'
                  }`}
                >
                  <span className="flex min-w-0 flex-col">
                    <span className="font-medium text-slate-800">
                      v{v?.version}
                      {isCurrent && (
                        <Badge className="ml-2 border border-green-300 bg-green-50 text-green-700">
                          {t('document.current')}
                        </Badge>
                      )}
                    </span>
                    <span className="truncate text-xs text-slate-500">
                      {v?.file?.originalName}
                      {v?.author?.name ? ` · ${v.author.name}` : ''}
                      {v?.createdAt
                        ? ` · ${format(new Date(v.createdAt), 'dd MMM yyyy HH:mm')}`
                        : ''}
                    </span>
                  </span>
                  <span className="flex shrink-0 items-center gap-1">
                    {v?.id && historyDoc && (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 w-7 p-0"
                        title={t('downloadDocument')}
                        disabled={downloadVersionMutation.isPending}
                        onClick={() =>
                          downloadVersion(
                            historyDoc.id,
                            v.id,
                            v?.file?.originalName ?? historyDoc?.name,
                          )
                        }
                      >
                        <Download className="h-3.5 w-3.5" />
                      </Button>
                    )}
                    {!isCurrent && canUpdate && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7"
                        disabled={rollbackDocumentMutation.isPending}
                        onClick={() => void handleRollback(v.id)}
                      >
                        <RotateCcw className="mr-1 h-3.5 w-3.5" />
                        {t('document.rollback')}
                      </Button>
                    )}
                  </span>
                </li>
              );
            })}
          </ul>
        </DialogContent>
      </Dialog>

      {/* Break-glass request (auditor side) */}
      <Dialog open={!!requestDoc} onOpenChange={(o) => !o && setRequestDoc(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldAlert className="h-4 w-4 text-red-500" />
              {t('document.requestDialogTitle')}
            </DialogTitle>
            <DialogDescription>{t('document.requestDialogDescription')}</DialogDescription>
          </DialogHeader>
          <div className="my-2">
            <label className="mb-2 block text-sm font-medium">
              {t('document.reasonLabel')}
            </label>
            <Textarea
              value={requestReason}
              onChange={(e) => setRequestReason(e.target.value)}
              placeholder={t('document.reasonPlaceholder')}
              rows={3}
              maxLength={1000}
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setRequestDoc(null)}
              disabled={requestBreakGlassMutation.isPending}
            >
              {t('cancel')}
            </Button>
            <Button
              onClick={() => void handleRequest()}
              disabled={!requestReason.trim() || requestBreakGlassMutation.isPending}
            >
              {requestBreakGlassMutation.isPending
                ? t('processing')
                : t('document.sendRequest')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Break-glass grant (approver side) */}
      <Dialog open={!!grantDoc} onOpenChange={(o) => !o && setGrantDoc(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldAlert className="h-4 w-4 text-amber-600" />
              {t('document.grantDialogTitle')}
            </DialogTitle>
            <DialogDescription>{t('document.grantDialogDescription')}</DialogDescription>
          </DialogHeader>
          <div className="my-2 space-y-4">
            <div>
              <label className="mb-2 block text-sm font-medium">
                {t('document.auditorLabel')}
              </label>
              <UniversalCombobox
                url="/users"
                listUrl="/users/select-box"
                labelKey="name"
                valueKey="id"
                selectedId={grantAuditorId}
                onSelectItem={(item) => setGrantAuditorId(item?.['id'] as number | undefined)}
                placeholder={t('selectUser')}
              />
            </div>
            <div>
              <label className="mb-2 block text-sm font-medium">
                {t('document.reasonLabel')}
              </label>
              <Textarea
                value={grantReason}
                onChange={(e) => setGrantReason(e.target.value)}
                placeholder={t('document.reasonPlaceholder')}
                rows={3}
                maxLength={1000}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setGrantDoc(null)}
              disabled={grantBreakGlassMutation.isPending}
            >
              {t('cancel')}
            </Button>
            <Button
              onClick={() => void handleGrant()}
              disabled={
                !grantAuditorId || !grantReason.trim() || grantBreakGlassMutation.isPending
              }
            >
              {grantBreakGlassMutation.isPending
                ? t('processing')
                : t('document.grant4h')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <Dialog open={!!deleteDoc} onOpenChange={(o) => !o && setDeleteDoc(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Trash2 className="h-4 w-4 text-red-500" />
              {t('document.deleteDialogTitle')}
            </DialogTitle>
            <DialogDescription>
              {t('document.deleteDialogDescription', { name: deleteDoc?.name ?? '' })}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteDoc(null)}
              disabled={deleteDocumentMutation.isPending}
            >
              {t('cancel')}
            </Button>
            <Button
              variant="destructive"
              onClick={() => void handleDelete()}
              disabled={deleteDocumentMutation.isPending}
            >
              {deleteDocumentMutation.isPending ? t('processing') : t('delete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
